import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export enum InvitationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export enum InvitationRecipientType {
  EMAIL = 'EMAIL',
  MOBILE = 'MOBILE',
}

export interface InvitationItem {
  _id: string;
  recipient: string;
  recipientType: InvitationRecipientType;
  studentName?: string;
  role?: string;
  invitedBy?: {
    _id: string;
    username: string;
    profile?: {
      personal?: {
        firstName?: string;
        lastName?: string;
      };
    };
  };
  liveClass?: {
    _id: string;
    title: string;
    slug?: string;
  };
  course?: {
    _id: string;
    title: string;
    slug?: string;
  };
  status: InvitationStatus;
  token: string;
  customMessage?: string;
  expiresAt: string | Date;
  acceptedAt?: string | Date;
  acceptedUser?: any;
  createdAt: string | Date;
}

export interface InvitationsResponse {
  docs: InvitationItem[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
}

export interface CreateInvitationPayload {
  recipients: string[] | string;
  studentName?: string;
  role?: string;
  liveClassId?: string;
  courseId?: string;
  customMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvitationService {
  private http = inject(HttpClient);
  private baseUrl = environment.host;

  createInvitations(
    payload: CreateInvitationPayload,
  ): Observable<{
    success: boolean;
    createdCount: number;
    invitations: InvitationItem[];
    errors?: any[];
  }> {
    return this.http.post<any>(`${this.baseUrl}/api/v1/invitations`, payload);
  }

  getInvitations(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    liveClassId?: string;
    courseId?: string;
  }): Observable<InvitationsResponse> {
    let url = `${this.baseUrl}/api/v1/invitations?page=${params?.page || 1}&limit=${params?.limit || 10}`;
    if (params?.status) url += `&status=${encodeURIComponent(params.status)}`;
    if (params?.search) url += `&search=${encodeURIComponent(params.search)}`;
    if (params?.liveClassId)
      url += `&liveClassId=${encodeURIComponent(params.liveClassId)}`;
    if (params?.courseId)
      url += `&courseId=${encodeURIComponent(params.courseId)}`;

    return this.http.get<InvitationsResponse>(url);
  }

  getInvitationByToken(token: string): Observable<InvitationItem> {
    return this.http.get<InvitationItem>(
      `${this.baseUrl}/api/v1/invitations/token/${encodeURIComponent(token)}`,
    );
  }

  acceptInvitation(token: string): Observable<{
    success: boolean;
    message?: string;
    invitation: InvitationItem;
  }> {
    return this.http.post<any>(`${this.baseUrl}/api/v1/invitations/accept`, {
      token,
    });
  }

  resendInvitation(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<any>(
      `${this.baseUrl}/api/v1/invitations/${id}/resend`,
      {},
    );
  }

  cancelInvitation(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<any>(`${this.baseUrl}/api/v1/invitations/${id}`);
  }
}
